import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState, Step, StepVariable } from '../../types/store';
import logger from '@/services/logger';

// Define our actual state with a Record for steps
export interface StepsState {
  steps: Record<string, Step>;
  stepGroups: Record<string, {
    id: string;
    name: string;
    description: string;
    steps: string[];
    isDefault: boolean;
    createdAt: string;
    updatedAt: string;
  }>;
  activeStepId: string | null;
  activeGroupId: string | null;
  stepOrder: string[];
  isProcessing: boolean;
}

// Initial state
const initialState: StepsState = {
  steps: {},
  stepGroups: {},
  activeStepId: null,
  activeGroupId: null,
  stepOrder: [],
  isProcessing: false
};

// Create the slice
const stepsSlice = createSlice({
  name: 'steps',
  initialState,
  reducers: {
    // Add a new step
    addStep: (state, action: PayloadAction<Omit<Step, 'id' | 'order'>>) => {
      const id = crypto.randomUUID();
      const order = state.stepOrder.length;
      
      state.steps[id] = {
        ...action.payload,
        id,
        order,
        isEditing: false,
        output: '',
        variables: [],
        dependencies: [],
        responseSummary: '',
      };
      
      // Add to order array
      state.stepOrder.push(id);
      
      logger.info('Step added', { 
        module: 'steps',
        data: { stepId: id, name: action.payload.name }
      });
    },
    
    // Update an existing step
    updateStep: (state, action: PayloadAction<Partial<Step> & { id: string }>) => {
      const { id, ...changes } = action.payload;
      
      if (state.steps[id]) {
        state.steps[id] = { ...state.steps[id], ...changes };
        logger.info('Step updated', { 
          module: 'steps',
          data: { stepId: id, name: state.steps[id].name }
        });
      }
    },
    
    // Delete a step
    deleteStep: (state, action: PayloadAction<string>) => {
      const stepId = action.payload;
      
      if (state.steps[stepId]) {
        // Remove step from dependencies in other steps
        Object.values(state.steps).forEach(step => {
          if (step.dependencies.includes(stepId)) {
            step.dependencies = step.dependencies.filter(id => id !== stepId);
          }
        });
        
        // Remove from step order
        state.stepOrder = state.stepOrder.filter(id => id !== stepId);
        
        // Remove step from any groups
        Object.values(state.stepGroups).forEach(group => {
          group.steps = group.steps.filter(id => id !== stepId);
        });
        
        // Delete the step
        delete state.steps[stepId];
        
        // If deleted step was active, clear selection
        if (state.activeStepId === stepId) {
          state.activeStepId = null;
        }
        
        logger.info('Step deleted', { module: 'steps', data: { stepId } });
      }
    },
    
    // Reorder steps
    reorderSteps: (state, action: PayloadAction<{ stepId: string, newOrder: number }>) => {
      const { stepId, newOrder } = action.payload;
      
      if (state.steps[stepId] && newOrder >= 0 && newOrder < state.stepOrder.length) {
        // Remove from current position
        const currentOrder = state.stepOrder.indexOf(stepId);
        if (currentOrder !== -1) {
          state.stepOrder.splice(currentOrder, 1);
          
          // Insert at new position
          state.stepOrder.splice(newOrder, 0, stepId);
          
          // Update order of all steps
          state.stepOrder.forEach((id, idx) => {
            if (state.steps[id]) {
              state.steps[id].order = idx;
            }
          });
          
          logger.info('Step reordered', { 
            module: 'steps',
            data: { stepId, oldOrder: currentOrder, newOrder }
          });
        }
      }
    },
    
    // Set active step
    setActiveStep: (state, action: PayloadAction<string | null>) => {
      state.activeStepId = action.payload;
    },
    
    // Toggle step active status
    toggleStepActive: (state, action: PayloadAction<string>) => {
      const stepId = action.payload;
      
      if (state.steps[stepId]) {
        state.steps[stepId].isActive = !state.steps[stepId].isActive;
        logger.info(`Step ${state.steps[stepId].isActive ? 'activated' : 'deactivated'}`, { 
          module: 'steps',
          data: { stepId, name: state.steps[stepId].name }
        });
      }
    },
    
    // Toggle step editing mode
    toggleStepEditing: (state, action: PayloadAction<string>) => {
      const stepId = action.payload;
      
      if (state.steps[stepId]) {
        state.steps[stepId].isEditing = !state.steps[stepId].isEditing;
      }
    },
    
    // Add step variable
    addStepVariable: (state, action: PayloadAction<{ stepId: string, variable: StepVariable }>) => {
      const { stepId, variable } = action.payload;
      
      if (state.steps[stepId]) {
        state.steps[stepId].variables.push(variable);
        logger.info('Step variable added', {
          module: 'steps',
          data: { stepId, variableName: variable.name }
        });
      }
    },
    
    // Add step dependency
    addStepDependency: (state, action: PayloadAction<{ stepId: string, dependsOnId: string }>) => {
      const { stepId, dependsOnId } = action.payload;
      
      if (state.steps[stepId] && !state.steps[stepId].dependencies.includes(dependsOnId)) {
        state.steps[stepId].dependencies.push(dependsOnId);
        logger.info('Step dependency added', { 
          module: 'steps',
          data: { stepId, dependsOnId }
        });
      }
    },
    
    // Remove step dependency
    removeStepDependency: (state, action: PayloadAction<{ stepId: string, dependsOnId: string }>) => {
      const { stepId, dependsOnId } = action.payload;
      
      if (state.steps[stepId]) {
        state.steps[stepId].dependencies = state.steps[stepId].dependencies.filter(id => id !== dependsOnId);
        logger.info('Step dependency removed', { 
          module: 'steps',
          data: { stepId, dependsOnId }
        });
      }
    },
    
    // Set step output
    setStepOutput: (state, action: PayloadAction<{ stepId: string, output: string }>) => {
      const { stepId, output } = action.payload;
      
      if (state.steps[stepId]) {
        state.steps[stepId].output = output;
        logger.info('Step output set', { module: 'steps', data: { stepId } });
      }
    },
    
    // Create a new step group
    addStepGroup: (state, action: PayloadAction<{
      name: string;
      description: string;
      steps: string[];
      isDefault?: boolean;
    }>) => {
      const id = crypto.randomUUID();
      const timestamp = new Date().toISOString();
      
      state.stepGroups[id] = {
        id,
        name: action.payload.name,
        description: action.payload.description,
        steps: action.payload.steps,
        isDefault: action.payload.isDefault || false,
        createdAt: timestamp,
        updatedAt: timestamp
      };
      
      logger.info('Step group added', {
        module: 'steps',
        data: { groupId: id, name: action.payload.name }
      });
    },
    
    // Update step group
    updateStepGroup: (state, action: PayloadAction<{
      id: string;
      name?: string;
      description?: string;
      steps?: string[];
      isDefault?: boolean;
    }>) => {
      const { id, ...changes } = action.payload;
      
      if (state.stepGroups[id]) {
        state.stepGroups[id] = {
          ...state.stepGroups[id],
          ...changes,
          updatedAt: new Date().toISOString()
        };
        
        logger.info('Step group updated', {
          module: 'steps',
          data: { groupId: id, name: state.stepGroups[id].name }
        });
      }
    },
    
    // Delete step group
    deleteStepGroup: (state, action: PayloadAction<string>) => {
      const groupId = action.payload;
      
      if (state.stepGroups[groupId]) {
        delete state.stepGroups[groupId];
        
        if (state.activeGroupId === groupId) {
          state.activeGroupId = null;
        }
        
        logger.info('Step group deleted', { module: 'steps', data: { groupId } });
      }
    },
    
    // Set active group
    setActiveGroup: (state, action: PayloadAction<string | null>) => {
      state.activeGroupId = action.payload;
    },
    
    // Toggle processing state
    setProcessing: (state, action: PayloadAction<boolean>) => {
      state.isProcessing = action.payload;
    }
  },
});

// Export actions
export const {
  addStep,
  updateStep,
  deleteStep,
  reorderSteps,
  setActiveStep,
  toggleStepActive,
  toggleStepEditing,
  addStepVariable,
  addStepDependency,
  removeStepDependency,
  setStepOutput,
  addStepGroup,
  updateStepGroup,
  deleteStepGroup,
  setActiveGroup,
  setProcessing
} = stepsSlice.actions;

// Selectors
export const selectAllSteps = (state: RootState) => Object.values(state.steps.steps);
export const selectActiveSteps = (state: RootState) => 
  Object.values(state.steps.steps)
    .filter(step => step.isActive)
    .sort((a, b) => a.order - b.order);
export const selectActiveStep = (state: RootState) => 
  state.steps.activeStepId ? state.steps.steps[state.steps.activeStepId] : null;
export const selectStepsByOrder = (state: RootState) => 
  state.steps.stepOrder.map(id => state.steps.steps[id]).filter(Boolean);
export const selectAllGroups = (state: RootState) => Object.values(state.steps.stepGroups);
export const selectActiveGroup = (state: RootState) => 
  state.steps.activeGroupId ? state.steps.stepGroups[state.steps.activeGroupId] : null;
export const selectIsProcessing = (state: RootState) => state.steps.isProcessing;

// Export the reducer
export default stepsSlice.reducer;
